import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { analytics } from '@/analytics';
import { depthAxisRange } from '@/components/charts/generic/depthAxisRange';
import { useChartTheme } from '@/components/charts/theme';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import {
    DATE_RANGE_PRESETS,
    type DateRangePresetId,
    findMatchingPreset,
} from '@/components/molecules/DateRangePicker/DateRangePresets';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import { useDemoMode } from '@/contexts/demo/DemoContext';
import { useGetLocationDepthSeries } from '@/domain/depthSeries';
import type { CompareMetric } from '@/domain/locationCompare';
import { useGetLocationCompare } from '@/domain/locationCompare';
import { useDateRangePicker } from '@/hooks/useDateRangePicker';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';
import { toDateString } from '@/utils/date';
import { getDemoDisplayName } from '@/utils/demoMasking';
import { parseLocale } from '@/utils/localeFormatters';

import type { CageLegendItem } from './components/CageLegend';
import {
    buildMetricGroups,
    buildMetricOptions,
    pickSelectableMetric,
} from './helpers/availableMetrics';
import { buildCompareChartSpec, cageColor } from './helpers/buildCompareChart';
import { cagesForHourlyDepth, clampHourlyRange, hourlyDepthMetric } from './helpers/hourlyDepth';
import { useCageVisibility } from './hooks/useCageVisibility';
import LocationCompareView from './LocationCompareView';

const HOURLY_COMPARE_PRESETS: readonly DateRangePresetId[] = [
    '24h',
    'last3days',
    'last7days',
    'last14days',
];

function last7Days(): DateRange {
    return DATE_RANGE_PRESETS.find((preset) => preset.id === 'last7days')?.calculateDates() ?? {};
}

function depthValues(metric: CompareMetric | undefined, visibleIds: Set<number>): number[] {
    if (!metric) return [];
    return metric.dataPoints.flatMap((point) =>
        Object.entries(point.values).flatMap(([cageId, value]) =>
            visibleIds.has(Number(cageId)) && typeof value === 'number' ? [value] : []
        )
    );
}

interface LocationCompareContainerProps {
    clientId: number;
    locationId: number;
}

export default function LocationCompareContainer(props: LocationCompareContainerProps) {
    const { clientId, locationId } = props;
    const { isDemoMode } = useDemoMode();
    const compareTrackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { t, i18n } = useTranslation('locationCompare');
    const locale = parseLocale(i18n.language);
    const { enabled: hourlyDepth } = usePostHogFeatureFlag('hourly-depth');

    // Default to the last 4 weeks. The hook persists the choice in the URL.
    const { dateFrom, dateTo, onDateChange } = useDateRangePicker({
        key: 'compare',
        defaultPresetId: 'last4weeks',
    });

    const compareQuery = useGetLocationCompare(clientId, locationId, {
        from: dateFrom,
        to: dateTo,
    });
    const { data: compareResponse, isLoading, error } = compareQuery;

    // Demo masking happens here so the View doesn't need to know about it. A cage compared on its
    // fish group is named "Pen - Group", the format the cage pages' titles use; in demo mode the
    // group name is left out rather than shown unmasked.
    const data = useMemo(() => {
        if (!isDemoMode) {
            return {
                ...compareResponse,
                cages: compareResponse.cages.map((cage) =>
                    cage.activeFishGroup
                        ? { ...cage, name: `${cage.name} - ${cage.activeFishGroup.name}` }
                        : cage
                ),
            };
        }
        return {
            ...compareResponse,
            locationName: getDemoDisplayName(compareResponse.locationId, 'location', t),
            cages: compareResponse.cages.map((cage) => ({
                ...cage,
                name: getDemoDisplayName(cage.id, 'cage', t),
            })),
        };
    }, [compareResponse, isDemoMode, t]);

    const metricOptions = useMemo(
        () => buildMetricOptions(data.metrics, { hourlyDepth }),
        [data.metrics, hourlyDepth]
    );
    const metricGroups = useMemo(() => buildMetricGroups(metricOptions, t), [metricOptions, t]);
    const [selectedMetricId, setSelectedMetricId] = useState<string | undefined>(undefined);
    // Keep the last id even if it is temporarily disabled (e.g. date-range
    // shrink); pickSelectableMetric falls back for display and restores it
    // when that param has data again.
    const selectedOption = pickSelectableMetric(metricOptions, selectedMetricId);
    const hourlySelected = selectedOption?.id === 'environment.averageDepthHourly';
    const dailyDepthSelected = selectedOption?.id === 'environment.averageDepth';
    const hourlyRange = clampHourlyRange(dateFrom, dateTo, toDateString(new Date()));
    const depthQuery = useGetLocationDepthSeries(
        clientId,
        locationId,
        { from: hourlyRange.from, to: hourlyRange.to, resolution: 'hour' },
        { enabled: hourlyDepth && hourlySelected }
    );

    const chartCages = useMemo(() => {
        if (!hourlySelected) return data.cages;
        return cagesForHourlyDepth(data.cages, depthQuery.data.cages).map((cage) =>
            isDemoMode && data.cages.every((known) => known.id !== cage.id)
                ? { ...cage, name: getDemoDisplayName(cage.id, 'cage', t) }
                : cage
        );
    }, [hourlySelected, data.cages, depthQuery.data.cages, isDemoMode, t]);
    const { visibleIds, toggle: onToggleCage } = useCageVisibility(chartCages);
    // The legend swatch shows the colour the chart draws the cage in, dark lift included.
    const chartTheme = useChartTheme();
    const legendItems: CageLegendItem[] = useMemo(
        () =>
            chartCages.map((cage) => ({
                cageId: cage.id,
                label: cage.name,
                color: chartTheme.color(cageColor(cage.id, chartCages)),
            })),
        [chartCages, chartTheme]
    );

    const chartMetric: CompareMetric | undefined = hourlySelected
        ? hourlyDepthMetric(chartCages, depthQuery.data.cages)
        : selectedOption?.metric;
    const depthBounds =
        hourlyDepth && (hourlySelected || dailyDepthSelected)
            ? depthAxisRange(depthValues(chartMetric, visibleIds))
            : undefined;

    const chartSpec = useMemo(
        () =>
            buildCompareChartSpec({
                cages: chartCages,
                metric: chartMetric,
                visibleCageIds: visibleIds,
                locale,
                ...(hourlySelected ? { xAxisKind: 'hour' as const } : {}),
                ...(depthBounds
                    ? { beginAtZero: false, yMin: depthBounds.min, yMax: depthBounds.max }
                    : {}),
            }),
        [chartCages, chartMetric, visibleIds, locale, hourlySelected, depthBounds]
    );

    const visiblePenIds = useMemo(() => [...visibleIds], [visibleIds]);

    const trackCompareUsage = useCallback(
        (
            comparisonType: string | undefined,
            pens: number[],
            dateRange?: Pick<DateRange, 'dateFrom' | 'dateTo'>
        ) => {
            if (compareTrackTimeoutRef.current) {
                clearTimeout(compareTrackTimeoutRef.current);
            }

            compareTrackTimeoutRef.current = setTimeout(() => {
                analytics.comparisonUsed({
                    sourcePage: 'compare',
                    comparisonType,
                    timeFrameFrom: dateRange?.dateFrom ?? dateFrom,
                    timeFrameTo: dateRange?.dateTo ?? dateTo,
                    selectedPens: pens,
                });
            }, 500);
        },
        [dateFrom, dateTo]
    );

    useEffect(() => {
        return () => {
            if (compareTrackTimeoutRef.current) {
                clearTimeout(compareTrackTimeoutRef.current);
            }
        };
    }, []);

    const handleMetricChange = useCallback(
        (metricId: string) => {
            setSelectedMetricId(metricId);
            if (metricId === 'environment.averageDepthHourly') {
                const preset = findMatchingPreset(dateFrom, dateTo, {
                    include24h: true,
                    includeLast3Days: true,
                });
                // A custom range stays, even one that is not the last seven days. A range
                // longer than 31 days is shortened by the hourly request itself.
                const usesLongPreset = preset !== null && !HOURLY_COMPARE_PRESETS.includes(preset);
                if (!dateFrom || !dateTo || usesLongPreset) {
                    onDateChange(last7Days());
                }
            }
            trackCompareUsage(metricId, visiblePenIds);
        },
        [dateFrom, dateTo, onDateChange, trackCompareUsage, visiblePenIds]
    );

    const handleDateChange = useCallback(
        (range: DateRange) => {
            onDateChange(range);
            trackCompareUsage(selectedOption?.id, visiblePenIds, range);
        },
        [onDateChange, selectedOption?.id, trackCompareUsage, visiblePenIds]
    );

    const handleToggleCage = useCallback(
        (cageId: number) => {
            onToggleCage(cageId);
            const nextVisibleIds = visibleIds.has(cageId)
                ? [...visibleIds].filter((id) => id !== cageId)
                : [...visibleIds, cageId];
            trackCompareUsage(selectedOption?.id, nextVisibleIds);
        },
        [onToggleCage, selectedOption?.id, trackCompareUsage, visibleIds]
    );

    return (
        <LocationCompareView
            locationName={data.locationName}
            isLoading={isLoading || (hourlySelected && depthQuery.isLoading)}
            error={(error ?? (hourlySelected ? depthQuery.error : null)) as Error | null}
            retry={retryFailedQueries(compareQuery, depthQuery)}
            showDepthInfo={hourlyDepth}
            hourlyRangeClamped={hourlySelected && hourlyRange.clamped}
            hourlySelected={hourlySelected}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateChange={handleDateChange}
            metricGroups={metricGroups}
            selectedMetricId={selectedOption?.id}
            onMetricChange={handleMetricChange}
            chartSpec={chartSpec}
            legendItems={legendItems}
            visibleCageIds={visibleIds}
            onToggleCage={handleToggleCage}
        />
    );
}
